<script>
import Vue from 'vue'
import Component from 'vue-class-component'

import YearsDropdown from '@/components/YearsDropdown'
import ObservationCard from './ObservationCard'
import ObservationModal from './ObservationModal'

import { teacherObsHelper } from '@/helpers/teacherObsHelper'
import { onboardingHelper } from '@/helpers/onboardingHelper'

import EventBus from '@/event-bus'

import store from '@/store'

@Component({
    props: {
        userRole: String,
        fiscalYear: Number,
        teacherInfo: Array,
        observations: Array
    },
    components: {
        YearsDropdown,
        ObservationCard,
        ObservationModal
    },
    computed: {
        teacherFullName() {
            return `${this.teacherInfoDisplayed.FirstName} ${this.teacherInfoDisplayed.LastName}`
        },
        dropdownYears() {
            let years = this.teacherInfo.map(info => info.FiscalYear)
                                        .sort()
                                        .reverse()
            if (years.indexOf(2014) > -1) { 
                years.splice(years.indexOf(2014), 1) 
            }
            return years
        },
        fiscalYearDisplay() {
            return `${this.fiscalYear-1}-${this.fiscalYear}`
        },
        motpOptionDisplay() { 
            let motpOptions = this.teacherInfoDisplayed.MOTPOptionDescription
            if (motpOptions) {
                const optionArray = motpOptions.split(': ')
            return this.userRole === 'Teacher'
                ? `I selected ${optionArray[0]}, which includes ${optionArray[1]}`
                : `This teacher selected ${optionArray[0]}, which includes ${optionArray[1]}`
            } else {
                return false
            }
        },
        teacherInfoDisplayed() {
            return this.teacherInfo.filter(info => info.FiscalYear === this.fiscalYear)[0]
        },
        observationsDisplayed() {
            let observations = this.observations.filter(observation => observation.FiscalYear === this.fiscalYear)
            return observations
        },
        remainingObservations() {
            return this.teacherInfoDisplayed.expected_obs - this.observationsDisplayed.length > 0
                ? this.teacherInfoDisplayed.expected_obs - this.observationsDisplayed.length : 0
        },
        currentMotpScore() {
            return this.teacherInfo.find(info => info.FiscalYear === this.fiscalYear).SL_MOTP_Score
        },
        pastMotpScore() {
            return this.teacherInfo.find(info => info.FiscalYear === this.fiscalYear - 1)
                ? this.teacherInfo.find(info => info.FiscalYear === this.fiscalYear - 1).SL_MOTP_Score
                : null
        },
        steps() {
            return this.userRole === 'Teacher'
                ? teacherObsHelper().teacherSteps(this.currentMotpScore)
                : teacherObsHelper().adminSteps
        }
    }
})

export default class ObservationsWrapper extends Vue {
    dialogVisible = false
    selectedObservation = null

    created() {
      store.commit('currentPage', 'observations')
    }
    mounted() {
        EventBus.$on('start intro', () => {
            this.startIntro()
        })

        EventBus.$on('close observation modal', () => {
            this.selectedObservation = null
        })
        
        this.$nextTick(() => {
            if (localStorage.getItem('doneTeacherObsOnboarding')) {
                return
            } else {
                this.startIntro();
            }
        })
    }

    startIntro() {
        onboardingHelper().startIntro(
            introJs(), this, 'doneTeacherObsOnboarding'
        )
    }

    endIntro() {
        introJs().exit()
        if (this.userRole === 'Teacher') {
            EventBus.$emit('view teacher dashboard')
        }
    }

    pendingObservationHeading(observation) {
        return `Observation #${observation + this.observationsDisplayed.length}`
    }

    openObservation(observation) {
        this.dialogVisible = true
        this.selectedObservation = observation
        this.role = this.userRole

        this.$ga.event({
            eventCategory: 'Open Observation Modal',
            eventAction: this.selectedObservation.SchoolDBN,
            eventLabel: this.role
        })
    }
}
</script>

<template>
    <div class="observations">
        <div v-if="teacherInfoDisplayed">
            <div class="observations-heading">
                <span v-if="userRole !== 'Teacher'" class="admin">{{ teacherFullName }}: </span>
                <span :class="userRole !== 'Teacher' ? 'admin' : 'teacher'">OBSERVATIONS </span> 
                <YearsDropdown 
                    :dropdown-years="dropdownYears"
                    :user-role="userRole"
                />
                <router-link v-if="this.userRole !== 'Teacher'" 
                    :to="{name: 'teacher-dashboard', params: {id: teacherInfo[0].EmployeeID}}">
                    <el-button type="info"
                        @click="endIntro()">
                        Jump to Teacher Dashboard
                    </el-button>
                </router-link>
                <router-link v-else 
                    :to="{name: 'home'}">
                    <el-button type="info"
                        id="teacher-dash-button"
                        @click="endIntro()">
                        Jump to Teacher Dashboard
                    </el-button>
                </router-link>
            </div>
            <div
                class="no-selection" 
                v-if="!motpOptionDisplay"
            >
                <div 
                    class="empty-state"
                >
                    <div class="empty-state-content">
                        <img src="@/assets/images/error_24px.png" alt="information icon" />
                        <div>
                            <p>Looks like we don't have any data to display here.</p>
                            <p>To start exploring previous data <strong>use the Year dropdown</strong> above.</p>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else>
                <div class="observations-cards">
                    <p v-if="motpOptionDisplay">{{ motpOptionDisplay }}</p>
                    <ul id="cards-display">
                        <li v-for="observation in observationsDisplayed"
                            :key="observation.date"
                            @click="openObservation(observation)">
                            <ObservationCard :observation="observation"
                                v-if="observation.componentsMeasured.length > 0">
                            </ObservationCard>
                        </li>
                        <li class="card__expected"
                            v-for="observation in remainingObservations"
                            :key="observation"
                            :class="{ 'all-pending': observationsDisplayed.length === 0}">
                            <h3>{{ pendingObservationHeading(observation) }}</h3>
                        </li>
                    </ul>
                </div>
                <div class="table-notes">
                    <p v-if="fiscalYear === 2021">* Please note that observations for the 20-21 school year were added beginning in March 2021.</p>
                    <p v-if="fiscalYear === 2020">* Please note, teacher evaluations were suspended in mid-March 2020 due to COVID-19 so SY 19-20 MOTP is incomplete.</p>
                </div>
            </div>
            <Thermabar :current-score="currentMotpScore"
                :past-score="pastMotpScore"
                v-if="currentMotpScore"
                id="step4" />

            
            <ObservationModal
                v-if="selectedObservation"
                :dialogVisible="dialogVisible"
                :selectedObservation="selectedObservation" />
        </div>
        <div v-else>
            <h1 v-if="userRole === 'Teacher'">You have not selected an observation option for the current fiscal year. To see your data from previous years, navigate to your <router-link :to="'dashboard'">"Dashboard"</router-link> page.</h1>
            <h1 v-else>An observation option has not been made for this teacher for the current fiscal year. To see data for this teacher from previous years, navigate to their <router-link :to="{name: 'teacher-dashboard', params: {id: teacherInfo[0].EmployeeID}}">"Dashboard"</router-link> page.</h1>
        </div>
    </div>
</template>

<style scoped lang='scss'>
    .observations {
        z-index: -1;
        padding: 22px 70px;
        background-color: #ffffff;
        .observations-heading {
            margin-right: 5%;
            color: #613f75;
            margin-bottom: 16px;
            span.admin, span.teacher {
                font-family: 'museo_slab', sans-serif;
                font-size: 48px;
            }
            .el-select, a button {
                vertical-align: text-bottom;
            }
        }
        .observations-cards {
            margin-bottom: 1%;
            margin-top: 50px;
            padding-left: 92px;
            p {
                font-size: 20px;
                font-family: 'museo_sans', sans-serif;
                margin-bottom: 40px;
            }
            ul {
                list-style: none;
                padding: 0;
                margin-top: 40px;
            }
            > ul {
                margin: 0 0 29px 0;
                display: inline-flex;
                flex-wrap: wrap;
            }
            > ul li {
                box-sizing: border-box;
                &:not(:last-child) {
                    margin-right: 25px;
                    margin-bottom: 2%;
                }
                &.card__expected {
                    border: dashed 5px #979797;
                    padding: 10px;
                    width: 225px;
                    height: 268px;
                    font-family: 'museo_sans', sans-serif;
                    h3 {
                        text-align: center;
                        margin: 0;
                        font-size: 24px;
                    }
                }
            }
        }
    }
    .empty-state {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        font-size: .9375em;
        p {
            margin: 0;
            line-height: 1.5;
            font-family: Arial;
        }
        .empty-state-content {
            display: flex;
            align-items: flex-start;
            div {
                margin-left: 10px;
            }
        }
        @media all and (min-width: 600px) {
            height: 500px;
        }
    }
    .table-notes {
        margin-top: 1em;
        p {
            margin: 0;
            font-size: 15px;
            font-family: Arial;
        }
    }
</style>